"use client";

import { cn } from "@newxsystems/shared";
import { useEffect, useRef, useState, type ComponentType } from "react";
import { watchEffectVisibility } from "../effects/effect-manager";
import { isTextEffect, TEXT_EFFECT_LOADERS, type TextEffectProps } from "./TextFx";

/**
 * Heading animation layer for the effectful hero.
 *
 * Same three rules as the image overlays: the style's chunk is fetched only for the
 * style that is actually selected, it is built while the heading is still one viewport
 * away (so it is never a blank frame on arrival) and it plays once — leaving the viewport
 * neither unloads it nor replays it, so scrolling back shows the finished heading.
 * Reduced motion, an unselected effect or a failing chunk all leave the plain heading
 * text in place, which is also what server rendering emits.
 *
 * The wrapper stays in the layout while the effect is traded in and out, so the heading
 * box — and with it the hero's line breaks — does not move when the chunk lands.
 */
export const TextEffect = ({ effect, text }: { effect?: string | null; text: string }) => {
  const frameRef = useRef<HTMLSpanElement>(null);
  const [warm, setWarm] = useState(false);
  const [active, setActive] = useState(false);
  const [Loaded, setLoaded] = useState<ComponentType<TextEffectProps> | null>(null);
  // One shot per mount: after the first play the watcher stops mattering.
  const playedRef = useRef(false);

  useEffect(() => {
    setWarm(false);
    setActive(false);
    setLoaded(null);
    playedRef.current = false;

    const frame = frameRef.current;
    if (!frame || !isTextEffect(effect)) return undefined;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return undefined;

    return watchEffectVisibility(frame, {
      warm: () => setWarm(true),
      cool: () => {
        if (playedRef.current) return;
        setWarm(false);
        setLoaded(null);
      },
      play: () => {
        playedRef.current = true;
        setActive(true);
      },
      pause: () => {
        if (!playedRef.current) setActive(false);
      },
    });
  }, [effect]);

  useEffect(() => {
    if (!warm || !isTextEffect(effect)) return undefined;

    let cancelled = false;
    void TEXT_EFFECT_LOADERS[effect]()
      .then((module) => {
        if (!cancelled) setLoaded(() => module.default);
      })
      .catch(() => {
        // The heading is the message; the animation is decoration.
      });

    return () => {
      cancelled = true;
    };
  }, [warm, effect]);

  return (
    <span className={cn("heading-fx", Loaded && "heading-fx--on")} ref={frameRef}>
      {Loaded ? <Loaded active={active} text={text} /> : text}
    </span>
  );
};

TextEffect.displayName = "TextEffects::TextEffect";
